import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Castle, Home as HomeIcon, LayoutGrid, Mountain, Search } from "lucide-react";
import LocationSelects from "./LocationSelects";
import { useI18n } from "@/i18n/I18nProvider";
import { useCatalog } from "@/hooks/useData";

const TABS = ["sale", "rent", "wanted"];
const QUICK = [
  { key: "all", to: "/search", icon: LayoutGrid },
  { key: "commercial", to: "/search?category=commercial", icon: Building2 },
  { key: "residential", to: "/search?category=residential", icon: HomeIcon },
  { key: "villas", to: "/search?type=villa", icon: Castle },
  { key: "land", to: "/search?category=land", icon: Mountain },
];

function QuickLinks() {
  const { t } = useI18n();
  const label = (k) => (k === "all" ? t("heroQ.allTypes") : k === "villas" ? t("heroQ.villas") : t(`category.${k}`));
  return (
    <div className="mt-6 grid grid-cols-5 gap-2 max-w-xl mx-auto" data-testid="hero-quick-links">
      {QUICK.map(({ key, to, icon: Icon }) => (
        <Link key={key} to={to} data-testid={`hero-quick-${key}`} className="group flex flex-col items-center gap-1.5 text-xs sm:text-sm font-semibold text-navy">
          <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-gold transition-transform group-hover:-translate-y-0.5" strokeWidth={1.6} />
          {label(key)}
        </Link>
      ))}
    </div>
  );
}

export default function HeroSearch() {
  const { t } = useI18n();
  const catalog = useCatalog();
  const navigate = useNavigate();
  const [f, setF] = useState({ purpose: "sale", governorate_id: null, type: "", currency: "" });

  const submit = (e) => {
    e.preventDefault();
    if (f.purpose === "wanted") return navigate("/wanted");
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && params.set(k, v));
    navigate(`/search?${params}`);
  };
  const tabLabel = (p) => (p === "wanted" ? t("heroQ.wanted") : t(`purpose.${p}`));

  return (
    <div>
      <div className="flex justify-center gap-1 mb-3" role="tablist">
        {TABS.map((p) => (
          <button type="button" key={p} data-testid={`search-tab-${p === "sale" ? "buy" : p}`} onClick={() => setF({ ...f, purpose: p })}
            className={`rounded-full px-5 sm:px-7 h-9 text-sm font-bold transition-colors ${f.purpose === p ? "bg-gold text-white shadow-md shadow-gold/30" : "text-navy hover:text-gold"}`}>
            {tabLabel(p)}
          </button>
        ))}
      </div>
      <form onSubmit={submit} data-testid="hero-search-form"
        className="hero-search-card rounded-2xl p-3 sm:p-3 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto] items-center [&_label]:sr-only">
        <LocationSelects value={f} levels={1} prefix="search" anyLabel onChange={(v) => setF({ ...f, ...v })} />
        <select aria-label={t("heroQ.type")} data-testid="search-property-type-select" className="bay-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
          <option value="">{t("heroQ.type")}</option>
          {catalog && Object.entries(catalog.categories).map(([cat, types]) => (
            <optgroup key={cat} label={t(`category.${cat}`)}>{types.map((ty) => <option key={ty} value={ty}>{t(`type.${ty}`)}</option>)}</optgroup>
          ))}
        </select>
        <select aria-label={t("heroQ.price")} data-testid="search-currency-select" className="bay-input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
          <option value="">{t("heroQ.price")}</option>
          {(catalog?.currencies || []).map((c) => <option key={c} value={c}>{t(`currency.${c}`)}</option>)}
        </select>
        <button type="submit" data-testid="hero-search-submit-button" aria-label={t("hero.search")} className="btn-gold w-full sm:w-14 h-11 justify-center">
          <Search className="w-5 h-5" /><span className="sm:hidden">{t("hero.search")}</span>
        </button>
      </form>
      <QuickLinks />
    </div>
  );
}
